import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { Alert, Button, Form } from "@curves/ui-base";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { ReadTextField } from "@icoach/components";
import { isFunction } from "@util/utils";
import CreditCardEditor from "@icoach/components/creditCard/creditCardEditor/CreditCardEditor";
import { PAYMENT_SOURCE_TYPE_MAP } from "@icoach/constants";
import { useMemberPaymentApi } from "@apis/useMemberPaymentApi";
import useOptions from "@util/hook/useOptions";

const getDefaultCreditCardData = (raw) => ({
	memberPaymentID: raw?.memberPaymentID || raw?.payment?.memberPaymentID || 0,
	sourceType: raw?.sourceType || PAYMENT_SOURCE_TYPE_MAP.DEVICEORDER,
	memberID: raw?.memberID || 0,
	creditCardBankName: raw?.creditCardBankName || "",
	creditCardType: raw?.creditCardType || 1, // 1: VISA, 2: MasterCard, 3: JCB
	creditCardNo: raw?.creditCardNo || "",
	cardExpiryDate: raw?.cardExpiryDate || "",
	cardName: raw?.cardName || "",
	cardRelation: raw?.cardRelation || ""
});

const SmartFitnessBandEditCreditCardDialog = forwardRef(({ open, sourceData, refresh, onClose }, ref) => {
	if (!open) return null;

	const { memberID, memberName, memberNo } = sourceData || {};

	// 取得 options
	const { getMemberPaymentOptionsApi } = useMemberPaymentApi();
	const { options, getOptionsApi } = useOptions({
		getOptionsApi: () => getMemberPaymentOptionsApi(memberID),
		isFetchOnMount: false
	});

	useEffect(() => {
			if (open) {
				getOptionsApi();
			}
		},
		// eslint-disable-next-line
		[open]);

	const componentRef = useRef(null);

	const handleSubmit = () => {
		const { onSubmit } = componentRef.current || {};
		isFunction(onSubmit) && onSubmit();
	};

	const handleSuccess = () => {
		isFunction(refresh) && refresh();
		onClose();
	};

	const defaultPaymentData = useMemo(() => getDefaultCreditCardData(sourceData), [sourceData]);

	return (
		<Dialog
			className={clsx("simple-content", "dialog", "payment-dialog")}
			PaperProps={{ className: "wx-70rem", component: Form }}
			onSubmit={handleSubmit}
			open={open}
			fullWidth
		>
			<GradientColorHeader onClose={onClose}>智能系統信用卡變更單</GradientColorHeader>
			<DialogContent>
				<Grid spacing={2} container>
					<Grid xs={12} item>
						<Alert color="info" severity="info" variant="filled">
							為保障會員權益，每筆付款資料皆為獨立授權。本次變更僅適用於此筆付款設定，不會同步其他付款項目，請視需要分別進行變更與授權。
						</Alert>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label={"會員"}>{memberName}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label={"會員編號"}>{memberNo}</ReadTextField>
					</Grid>
					<Grid xs={12} item>
						<CreditCardEditor
							ref={componentRef}
							sourceData={defaultPaymentData}
							options={options}
							refresh={handleSuccess}
							showCardRelation
							showBankName
							required
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined">
					取消編輯
				</Button>
				<Button type={"submit"} variant="contained">
					確認送出
				</Button>
			</DialogActions>
		</Dialog>
	);
});

export default SmartFitnessBandEditCreditCardDialog;